import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Rocket, Plus, X, Loader2, Building2, Send, CheckCircle2,
  Copy, KeyRound, Link as LinkIcon, AlertCircle, Calendar
} from 'lucide-react';
import { toast } from 'sonner';
import { QuickLinkBlock } from './QuickLinkBlock';
import { PLAN_CATALOG, IMPLANT_PLAN_IDS, fetchShelfIds } from '@/constants/plans';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ALL_PLAN_OPTIONS = PLAN_CATALOG
  .filter((p) => IMPLANT_PLAN_IDS.includes(p.id))
  .map((p) => ({ id: p.id, label: p.label, price: p.price, hint: p.limit_text }));

const formatMoney = (v) => `R$ ${(v ?? 0).toFixed(2).replace('.', ',')}`;
const formatBR = (iso) => iso ? new Date(iso).toLocaleDateString('pt-BR') : '—';

export const PartnerImplantClientsTab = ({ token, endpoints, headers, sellerType = 'partner' }) => {
  const submitUrl = endpoints ? `${API}${endpoints.submit}` : `${API}/partners/panel/${token}/implant-client`;
  const listUrl = endpoints ? `${API}${endpoints.list}` : `${API}/partners/panel/${token}/clients`;
  const extraHeaders = headers || {};
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    company_name: '', cnpj: '', owner_name: '', owner_email: '', owner_phone: '', plan_id: 'enterprise'
  });
  const [closingMode, setClosingMode] = useState('boleto');
  const [discountPct, setDiscountPct] = useState(0);
  const [discountReason, setDiscountReason] = useState('');
  const [discountOptions, setDiscountOptions] = useState([]);
  const [result, setResult] = useState(null);
  const [quickLink, setQuickLink] = useState(null);
  const [quickLink30d, setQuickLink30d] = useState(null);
  const [quickLinkStats, setQuickLinkStats] = useState(null);
  const [shelfIds, setShelfIds] = useState(['enterprise', 'elite', 'five_stars']);
  const PLAN_OPTIONS = ALL_PLAN_OPTIONS.filter(p => shelfIds.includes(p.id));

  useEffect(() => { fetchShelfIds().then(setShelfIds); }, []);

  useEffect(() => {
    fetch(`${API}/discount-options`).then(r => r.json())
      .then(d => setDiscountOptions(d.options || [])).catch(() => {});
  }, []);

  const fetchClients = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(listUrl, { headers: extraHeaders });
      const data = await res.json();
      setClients(data.items || []);
      if (data.quick_link) setQuickLink(data.quick_link);
      if (data.quick_link_30d) setQuickLink30d(data.quick_link_30d);
      if (data.quick_link_stats) setQuickLinkStats(data.quick_link_stats);
    } catch {
      toast.error('Erro ao carregar clientes');
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listUrl]);

  useEffect(() => { fetchClients(); }, [fetchClients]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.company_name.trim() || !form.owner_name.trim() || !form.owner_phone.trim()) {
      toast.error('Preencha empresa, nome do dono e WhatsApp');
      return;
    }
    if (form.plan_id !== 'free' && !form.cnpj.trim()) {
      toast.error('Informe o CPF/CNPJ do cliente para gerar a cobrança');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(submitUrl, {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...extraHeaders },
        body: JSON.stringify({
          ...form, closing_mode: form.plan_id === 'free' ? 'trial' : closingMode,
          discount_pct: discountPct || null, discount_reason: discountReason || null,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'fail');
      }
      const data = await res.json();
      setResult({ ...data, company_name: form.company_name });
      setShowForm(false);
      setForm({ company_name: '', cnpj: '', owner_name: '', owner_email: '', owner_phone: '', plan_id: 'enterprise' });
      setDiscountPct(0); setDiscountReason('');
      fetchClients();
    } catch (err) {
      toast.error(err.message || 'Erro ao implantar');
    } finally { setSubmitting(false); }
  };

  if (loading) {
    return <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-[#C5A059]" /></div>;
  }

  return (
    <main className="max-w-6xl mx-auto px-3 sm:px-6 py-4 sm:py-6 space-y-4 sm:space-y-6" data-testid="partner-implant-tab">
      {/* Cabeçalho */}
      <Card className="border-0 bg-gradient-to-br from-[#0A2647] to-[#0e3260] shadow-lg">
        <CardContent className="pt-6 pb-5">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-[#C5A059]/20 flex items-center justify-center flex-shrink-0">
              <Rocket className="w-6 h-6 text-[#C5A059]" />
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-bold text-white mb-1">Implantar novo cliente</h2>
              <p className="text-sm text-slate-300 leading-relaxed">
                Você cadastra e já fecha o negócio na hora: <strong>Boleto (3 dias), PIX ou Trial de 7 dias</strong>.
                O cliente recebe o acesso e a placa com QR Code <strong>automaticamente no WhatsApp</strong>.
                {sellerType === 'partner'
                  ? ' Sua comissão recorrente entra automaticamente no Asaas.'
                  : ' Sua comissão de implantação é gerada no 1º pagamento do cliente.'}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Links Relâmpago pessoais */}
      {quickLink && (
        <Card className="border-2 border-dashed border-[#C5A059]/50 bg-amber-50/40 shadow-sm" data-testid="quick-link-card">
          <CardContent className="pt-5 pb-4 space-y-3">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#C5A059]/20 flex items-center justify-center flex-shrink-0">
                <LinkIcon className="w-5 h-5 text-[#C5A059]" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-bold text-[#0A2647]">⚡ Seu Link Relâmpago</p>
                <p className="text-xs text-slate-600 mt-0.5 leading-relaxed">
                  Envie para o cliente: ele se cadastra sozinho em <strong>2 passos</strong> (3 campos + CPF)
                  e a venda <strong>entra na sua carteira automaticamente</strong>. Se ele parar no meio, nosso sistema
                  resgata o lead pelo WhatsApp por você. Escolha o link conforme a negociação.
                </p>
              </div>
            </div>

            {quickLinkStats && (
              <div className="flex gap-2 flex-wrap" data-testid="quick-link-stats">
                <span className="inline-flex items-center gap-1 bg-white border border-slate-200 rounded-full px-3 py-1 text-[11px] font-semibold text-[#0A2647]">
                  👀 {quickLinkStats.clicks_30d} clique{quickLinkStats.clicks_30d === 1 ? '' : 's'} (30d)
                </span>
                <span className="inline-flex items-center gap-1 bg-white border border-slate-200 rounded-full px-3 py-1 text-[11px] font-semibold text-[#0A2647]">
                  ⚡ {quickLinkStats.clicks_7d} nos últimos 7 dias
                </span>
                <span className="inline-flex items-center gap-1 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1 text-[11px] font-semibold text-emerald-700">
                  ✅ {quickLinkStats.signups} cadastro{quickLinkStats.signups === 1 ? '' : 's'} pelo link
                </span>
              </div>
            )}

            <QuickLinkBlock
              link={quickLink}
              testid="quick-link"
              titulo="⚡ Link Relâmpago – boleto em 3 dias"
              descricao="O cliente entra no plano pago e o primeiro boleto vence em 3 dias. Ideal para quem já decidiu fechar."
              mensagemWhatsapp={'Ative o Falo com o Dono na sua empresa em 1 minuto ⭐\n\nCadastro rápido (só 3 campos) e sua placa com QR Code chega na hora no seu WhatsApp:'}
            />

            {quickLink30d && (
              <QuickLinkBlock
                link={quickLink30d}
                testid="quick-link-30d"
                destaque
                titulo="🎁 Link 30 dias grátis – 1º boleto vence em 30 dias"
                descricao="O cliente usa o primeiro mês por conta da casa e só paga no 30º dia. Ideal para quebrar objeção de preço."
                mensagemWhatsapp={'Experimente o Falo com o Dono por 30 dias grátis ⭐\n\nCadastro rápido (só 3 campos), sua placa com QR Code chega na hora no WhatsApp e o primeiro boleto só vence em 30 dias:'}
              />
            )}

            <p className="text-[11px] text-slate-600 bg-white/80 border border-slate-200 rounded-lg px-3 py-2" data-testid="quick-link-cpf-note">
              Nos dois links, o cadastro só vira assinatura depois que o cliente informa CPF ou CNPJ.
            </p>
          </CardContent>
        </Card>
      )}

      {!showForm ? (
        <Button
          onClick={() => setShowForm(true)}
          className="bg-[#C5A059] hover:bg-[#b08e4a] text-white gap-2 h-12 px-6 font-semibold"
          data-testid="new-client-btn"
        >
          <Plus className="w-4 h-4" /> Implantar novo cliente
        </Button>
      ) : (
        <Card className="border-2 border-[#C5A059]/30 bg-white shadow-md">
          <CardContent className="pt-6 pb-5 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-bold text-[#0A2647]">Dados do novo cliente</h3>
              <button onClick={() => setShowForm(false)} className="p-1 hover:bg-slate-100 rounded"><X className="w-4 h-4" /></button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-3">
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-semibold text-slate-700">Nome da empresa *</Label>
                  <Input value={form.company_name} onChange={e => setForm({ ...form, company_name: e.target.value })} className="mt-1" data-testid="impl-company-name" />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-slate-700">Nome do dono *</Label>
                  <Input value={form.owner_name} onChange={e => setForm({ ...form, owner_name: e.target.value })} className="mt-1" data-testid="impl-owner-name" />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-semibold text-slate-700">WhatsApp do dono *</Label>
                  <Input value={form.owner_phone} onChange={e => setForm({ ...form, owner_phone: e.target.value })} placeholder="(11) 99999-9999" className="mt-1" data-testid="impl-phone" />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-slate-700">E-mail (opcional)</Label>
                  <Input type="email" value={form.owner_email} onChange={e => setForm({ ...form, owner_email: e.target.value })} placeholder="Sem e-mail? Ele entra pelo WhatsApp" className="mt-1" data-testid="impl-email" />
                </div>
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-700">Escolha o plano que o cliente vai pagar *</Label>
                <div className="mt-2 grid sm:grid-cols-3 gap-2">
                  {PLAN_OPTIONS.map(p => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => setForm({ ...form, plan_id: p.id })}
                      className={`p-3 rounded-xl border-2 text-left transition-all ${
                        form.plan_id === p.id ? 'border-[#C5A059] bg-amber-50/40 shadow-sm' : 'border-slate-200 hover:border-slate-300'
                      }`}
                      data-testid={`impl-plan-${p.id}`}
                    >
                      <p className="text-sm font-bold text-[#0A2647]">{p.label}</p>
                      <p className="text-base font-bold text-[#C5A059] mt-0.5">
                        {p.price > 0
                          ? <>{formatMoney(p.price)}<span className="text-[10px] text-slate-500 font-normal">/mês</span></>
                          : <span className="text-emerald-600">Grátis</span>
                        }
                      </p>
                      <p className="text-[10px] text-slate-500 mt-1">{p.hint}</p>
                    </button>
                  ))}
                </div>
              </div>
              {form.plan_id !== 'free' && (
                <>
                  <div>
                    <Label className="text-xs font-semibold text-slate-700">CPF/CNPJ do cliente * (para a cobrança)</Label>
                    <Input value={form.cnpj} onChange={e => setForm({ ...form, cnpj: e.target.value })} placeholder="00.000.000/0000-00" className="mt-1" data-testid="impl-cnpj" />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold text-slate-700">Fechamento *</Label>
                    <div className="mt-2 grid grid-cols-3 gap-2">
                      {[['boleto', '📄 Boleto (3 dias)'], ['pix', '⚡ PIX agora'], ['trial', '🎁 Trial 7 dias']].map(([m, label]) => (
                        <button key={m} type="button" data-testid={`impl-closing-${m}`}
                          onClick={() => setClosingMode(m)}
                          className={`py-2.5 rounded-lg text-xs sm:text-sm font-semibold border-2 transition-all ${closingMode === m ? 'border-[#C5A059] bg-amber-50/40 text-[#0A2647]' : 'border-slate-200 text-slate-500 hover:border-slate-300'}`}>
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>
                  {discountOptions.length > 0 && (
                    <div>
                      <Label className="text-xs font-semibold text-slate-700">Desconto recorrente (opcional)</Label>
                      <div className="mt-2 flex flex-wrap gap-2 items-center">
                        <button type="button" onClick={() => setDiscountPct(0)} data-testid="impl-discount-0"
                          className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${discountPct === 0 ? 'bg-[#0A2647] text-white border-[#0A2647]' : 'bg-white text-slate-600 border-slate-200'}`}>Sem desconto</button>
                        {discountOptions.map(o => (
                          <button key={o} type="button" onClick={() => setDiscountPct(o)} data-testid={`impl-discount-${o}`}
                            className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${discountPct === o ? 'bg-[#C5A059] text-white border-[#C5A059]' : 'bg-white text-slate-600 border-slate-200'}`}>{o}%</button>
                        ))}
                      </div>
                      {discountPct > 0 && (
                        <>
                          <Input placeholder="Motivo (ex: grupo 3 lojas)" value={discountReason}
                            onChange={e => setDiscountReason(e.target.value)} className="mt-2" data-testid="impl-discount-reason" />
                          <p className="text-[11px] text-amber-700 mt-1.5">
                            ⚠️ O desconto vale para sempre e {sellerType === 'partner' ? 'sua comissão recorrente reduz no mesmo percentual' : 'sua comissão de implantação reduz no mesmo percentual'}.
                          </p>
                        </>
                      )}
                    </div>
                  )}
                  {(() => {
                    const sel = PLAN_OPTIONS.find(p => p.id === form.plan_id);
                    const fin = sel ? sel.price * (1 - discountPct / 100) : 0;
                    return (
                      <div className="bg-slate-50 rounded-lg p-3 flex items-center justify-between" data-testid="impl-price-summary">
                        <span className="text-xs text-slate-600">{sel?.label} {discountPct > 0 && <span className="text-[#C5A059] font-bold">−{discountPct}%</span>}</span>
                        <span className="text-base font-bold text-[#0A2647]">{formatMoney(fin)}<span className="text-[10px] font-normal text-slate-400">/mês</span></span>
                      </div>
                    );
                  })()}
                </>
              )}
              {form.plan_id === 'free' ? (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 flex items-start gap-2 text-xs text-emerald-900" data-testid="impl-free-notice">
                  <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <p>
                    <strong>Plano Free:</strong> o cliente entra <strong>sem cobrança</strong> e ganha tempo pra testar o sistema.
                    Quando ele fizer upgrade pra um plano pago no painel, <strong>sua comissão entra automaticamente</strong> (atribuição preservada).
                  </p>
                </div>
              ) : (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2 text-xs text-amber-900">
                  <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <p>
                    {closingMode === 'trial' && <>Ao confirmar, o cliente ganha <strong>7 dias grátis</strong> e a 1ª cobrança vence no fim do teste (paga como preferir).</>}
                    {closingMode === 'boleto' && <>Ao confirmar, o plano <strong>ativa na hora</strong> e o boleto vence em <strong>3 dias</strong> — chega no WhatsApp e no e-mail do cliente.</>}
                    {closingMode === 'pix' && <>Ao confirmar, o plano <strong>ativa na hora</strong> e o QR do PIX aparece na tela para o cliente <strong>pagar agora</strong>.</>}
                  </p>
                </div>
              )}
              <Button type="submit" disabled={submitting} className="w-full bg-[#0A2647] hover:bg-[#0e3260] text-white gap-2 h-11 font-semibold" data-testid="submit-implant">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Confirmar e gerar acesso
              </Button>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Lista de clientes implantados */}
      {clients.length === 0 ? (
        <Card className="border-0 bg-white shadow-md">
          <CardContent className="py-12 text-center">
            <Rocket className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-500">Você ainda não implantou nenhum cliente. Comece agora!</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">Clientes implantados ({clients.length})</p>
          {clients.map(c => {
            const planMeta = PLAN_OPTIONS.find(p => p.id === c.plan);
            return (
              <Card key={c.id} className="border-0 bg-white shadow-md" data-testid={`client-${c.id}`}>
                <CardContent className="pt-5 pb-4">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-lg bg-[#C5A059]/15 flex items-center justify-center flex-shrink-0">
                        <Building2 className="w-5 h-5 text-[#C5A059]" />
                      </div>
                      <div>
                        <p className="text-base font-bold text-[#0A2647]">{c.name}</p>
                        <div className="flex gap-3 text-xs text-slate-500 mt-1 flex-wrap">
                          {c.cnpj && <span>CNPJ {c.cnpj}</span>}
                          <span>Plano: <strong className="text-[#0A2647]">{planMeta?.label || c.plan}</strong></span>
                          {c.subscription_status === 'trial' && c.trial_until && (
                            <span className="inline-flex items-center gap-1 text-amber-700">
                              <Calendar className="w-3 h-3" /> Trial até {formatBR(c.trial_until)}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <span className={`px-2.5 py-1 rounded-full text-[11px] font-semibold ${
                      c.subscription_status === 'active' ? 'bg-emerald-100 text-emerald-700' :
                      c.subscription_status === 'trial' ? 'bg-amber-100 text-amber-700' :
                      'bg-slate-100 text-slate-500'
                    }`}>
                      {c.subscription_status === 'active' ? 'Ativo' : c.subscription_status === 'trial' ? 'Em teste' : c.subscription_status}
                    </span>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Modal Resultado */}
      {result && (
        <div className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6 space-y-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                <CheckCircle2 className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-base font-bold text-[#0A2647]">Cliente implantado!</p>
                <p className="text-xs text-slate-500 mt-0.5">Entregue para <strong>{result.company_name}</strong>:</p>
              </div>
            </div>

            {/* Credenciais */}
            <div className="bg-slate-50 border-2 border-dashed border-slate-300 rounded-xl p-4 space-y-3">
              <p className="text-[10px] font-bold text-[#C5A059] uppercase tracking-wider flex items-center gap-1">
                <KeyRound className="w-3 h-3" /> Acesso ao Painel
              </p>
              <div>
                <p className="text-[10px] font-bold text-slate-500 uppercase mb-1">E-mail</p>
                <div className="flex items-center gap-2">
                  <p className="text-sm font-mono font-semibold text-slate-900 flex-1 break-all">{result.credentials.email}</p>
                  <Button size="sm" variant="outline" className="h-7 px-2" onClick={() => { navigator.clipboard.writeText(result.credentials.email); toast.success('Copiado'); }}>
                    <Copy className="w-3 h-3" />
                  </Button>
                </div>
              </div>
              <div>
                <p className="text-[10px] font-bold text-slate-500 uppercase mb-1">Senha</p>
                <div className="flex items-center gap-2">
                  <p className="text-base font-mono font-bold text-[#C5A059] flex-1">{result.credentials.password}</p>
                  <Button size="sm" variant="outline" className="h-7 px-2" onClick={() => { navigator.clipboard.writeText(result.credentials.password); toast.success('Copiado'); }}>
                    <Copy className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            </div>

            {/* PIX QR (fechamento na hora) */}
            {result.pix_qr?.encodedImage && (
              <div className="bg-emerald-50 border-2 border-dashed border-emerald-300 rounded-xl p-4 text-center space-y-2" data-testid="impl-pix-qr">
                <p className="text-[10px] font-bold text-emerald-700 uppercase tracking-wider">⚡ Cliente escaneia e paga agora</p>
                <img src={`data:image/png;base64,${result.pix_qr.encodedImage}`} alt="PIX QR" className="w-44 h-44 mx-auto border rounded-lg bg-white" />
                <Button size="sm" variant="outline" className="text-xs h-8" onClick={() => { navigator.clipboard.writeText(result.pix_qr.payload); toast.success('Código PIX copiado'); }}>
                  <Copy className="w-3 h-3 mr-1" /> Copiar código PIX
                </Button>
              </div>
            )}

            {/* Link de pagamento */}
            {result.payment_url && (
              <div className="bg-blue-50 border-2 border-dashed border-blue-300 rounded-xl p-4 space-y-2">
                <p className="text-[10px] font-bold text-blue-700 uppercase tracking-wider flex items-center gap-1">
                  <LinkIcon className="w-3 h-3" /> {result.closing_mode === 'boleto' ? `Boleto (vence em ${result.due_date})` : `1ª fatura · vence em ${result.due_date}`}
                  {result.discount_pct > 0 && <span className="text-[#C5A059]"> · com {result.discount_pct}% off</span>}
                </p>
                <div className="flex items-center gap-2">
                  <p className="text-xs font-mono text-slate-700 flex-1 break-all bg-white px-2 py-1.5 rounded border border-slate-200">{result.payment_url}</p>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" className="flex-1 bg-blue-600 hover:bg-blue-700 text-white gap-1 text-xs h-8" onClick={() => { navigator.clipboard.writeText(result.payment_url); toast.success('Link copiado'); }}>
                    <Copy className="w-3 h-3" /> Copiar link
                  </Button>
                  <Button size="sm" variant="outline" className="flex-1 gap-1 text-xs h-8" onClick={() => window.open(result.payment_url, '_blank')}>
                    Abrir
                  </Button>
                </div>
              </div>
            )}

            <Button
              onClick={() => {
                const isFree = result.is_free_plan || result.plan_id === 'free' || !result.plan_price;
                const priceStr = `R$ ${(result.final_price ?? result.plan_price ?? 0).toFixed(2).replace('.', ',')}`;
                const planLine = isFree
                  ? `Plano contratado: ${result.plan_name} (Grátis · até 10 avaliações/mês)`
                  : `Plano contratado: ${result.plan_name} (${priceStr}/mês${result.discount_pct > 0 ? ` · com ${result.discount_pct}% de desconto` : ''})`;
                let paymentLine;
                if (isFree) {
                  paymentLine = `Você pode começar a usar sem custo. Quando quiser desbloquear mais recursos, é só fazer upgrade pelo painel.`;
                } else if (result.closing_mode === 'trial') {
                  paymentLine = `Você tem 7 dias grátis para testar. A 1ª cobrança vence em ${result.due_date}:\n${result.payment_url || ''}`;
                } else if (result.closing_mode === 'boleto') {
                  paymentLine = `Seu plano já está ativo! O boleto vence em ${result.due_date}:\n${result.payment_url || ''}`;
                } else {
                  paymentLine = `Seu plano já está ativo! Pague sua 1ª mensalidade via PIX:\n${result.payment_url || ''}`;
                }
                const text = `Olá! Sua conta no Falo com o Dono está pronta!\n\n${planLine}\n\nAcesso ao Painel:\nE-mail: ${result.credentials.email}\nSenha: ${result.credentials.password}\nLogin: ${result.credentials.login_url}\n\n${paymentLine}`;
                navigator.clipboard.writeText(text);
                toast.success('Mensagem copiada para enviar ao cliente');
              }}
              className="w-full bg-[#0A2647] hover:bg-[#0e3260] text-white gap-2"
              data-testid="copy-implant-message"
            >
              <Copy className="w-4 h-4" /> Copiar mensagem pronta para o cliente
            </Button>
            <Button variant="outline" onClick={() => setResult(null)} className="w-full">Fechar</Button>
          </div>
        </div>
      )}
    </main>
  );
};

export default PartnerImplantClientsTab;
